"use client";

import { useState, useRef, useEffect } from "react";
import { usePathname } from "next/navigation";
import { m, AnimatePresence } from "framer-motion";
import { useTheme } from "@/context/ThemeContext";
import { analytics } from "@/lib/analytics/core";

const SHOW_TOGGLE = process.env.NEXT_PUBLIC_DISPLAY_MODE_TOGGLE !== "false";

const modes = [
  { key: "light" as const, label: "Light" },
  { key: "dark" as const, label: "Dark" },
  { key: "system" as const, label: "System" },
];

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="4" />
      <path strokeLinecap="round" d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
    </svg>
  );
}

function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path strokeLinecap="round" d="M8 21h8m-4-4v4" />
    </svg>
  );
}

const iconMap = { light: SunIcon, dark: MoonIcon, system: MonitorIcon };

const MD_BREAKPOINT = 768;

export function ThemeToggle() {
  const { theme, setTheme, autoSwitched } = useTheme();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const mql = window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`);
    setIsDesktop(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const isAccountPage = pathname?.startsWith("/account");
  if (!mounted || !SHOW_TOGGLE || !isDesktop || isAccountPage) return null;

  const ActiveIcon = iconMap[theme];

  return (
    <div ref={panelRef} className="fixed top-4 right-4 z-30">
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full right-0 mt-2 rounded-xl border border-(--border-light) bg-surface p-1.5 shadow-lg dark:shadow-black/40"
          >
            {modes.map((mode) => {
              const Icon = iconMap[mode.key];
              const isActive = theme === mode.key;
              return (
                <button
                  key={mode.key}
                  type="button"
                  onClick={() => { setTheme(mode.key); setOpen(false); analytics.track("theme_toggle", { theme: mode.key }); }}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-foreground hover:bg-(--surface-hover)"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {mode.label}
                </button>
              );
            })}
          </m.div>
        )}
      </AnimatePresence>

      <m.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Toggle theme"
        className="relative flex h-9 w-9 items-center justify-center rounded-full border border-(--border-light) bg-surface text-foreground shadow-md transition-colors hover:bg-(--surface-hover) dark:shadow-black/30"
        animate={autoSwitched ? {
          scale: [1, 1.25, 1, 1.2, 1],
          boxShadow: [
            "0 0 0 0 rgba(217,78,104,0)",
            "0 0 20px 8px rgba(217,78,104,0.4)",
            "0 0 0 0 rgba(217,78,104,0)",
            "0 0 16px 6px rgba(217,78,104,0.3)",
            "0 0 0 0 rgba(217,78,104,0)",
          ],
        } : {}}
        transition={autoSwitched ? { duration: 2, repeat: 1, ease: "easeInOut" } : {}}
      >
        {autoSwitched && (
          <m.span
            className="absolute inset-0 rounded-full border-2 border-primary"
            initial={{ scale: 1, opacity: 1 }}
            animate={{ scale: 2, opacity: 0 }}
            transition={{ duration: 1.5, repeat: 2, ease: "easeOut" }}
          />
        )}
        <ActiveIcon className="h-4 w-4" />
      </m.button>
    </div>
  );
}
